import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * InstantRequestFeed — provider-only widget showing currently-open instant
 * match requests. Provider must be marked available_now to receive them.
 * First to accept wins.
 */
export default function InstantRequestFeed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/instant-match/feed/me");
      setItems(data || []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    if (!user?.has_provider_profile) return;
    refresh();
    const t = setInterval(refresh, 3500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const accept = async (req) => {
    setBusy(req.request_id);
    try {
      const { data } = await api.post(`/instant-match/${req.request_id}/accept`);
      toast.success("Matched! Opening session…");
      navigate(`/bookings/${data.booking_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Already taken");
      refresh();
    } finally {
      setBusy(null);
    }
  };

  if (!user?.has_provider_profile || items.length === 0) return null;

  return (
    <div
      className="bg-gradient-to-r from-[#4A5D4E] to-[#5C7361] text-white rounded-2xl p-5 mb-6 shadow-md"
      data-testid="instant-feed"
    >
      <div className="flex items-center gap-2 mb-3">
        <Zap className="h-4 w-4 fill-white" />
        <span className="text-xs uppercase tracking-[0.16em] font-bold">Live instant requests</span>
        <span className="ml-2 text-[11px] bg-white/20 px-2 py-0.5 rounded-full">{items.length} waiting</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((r) => (
          <div
            key={r.request_id}
            className="bg-white/10 backdrop-blur rounded-xl p-3 flex items-center justify-between gap-3"
            data-testid={`instant-feed-item-${r.request_id}`}
          >
            <div className="min-w-0">
              <div className="font-display text-sm font-semibold truncate">{r.customer_name}</div>
              <div className="text-[11px] text-white/70">
                {r.duration_min} min · ${r.amount?.toFixed(0)} · {r.mode}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => accept(r)}
              disabled={busy === r.request_id}
              className="rounded-full bg-[#C05640] hover:bg-[#A64A37] text-white h-8 px-3 shrink-0"
              data-testid={`instant-feed-accept-${r.request_id}`}
            >
              {busy === r.request_id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                "Accept"
              )}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
